<template>
  <div class="vb-card" :class="'vb-card--' + variant">
    <b-row class="vb-card__wrapper">
      <b-col
        class="vb-card__element vb-card__element--image"
        v-if="card.image"
        cols="12"
        :lg="variant === 'horizontal' && '4'"
      >
        <div
          class="vb-card__image"
          :style="{
            'background-image': `url('${card.image}') ${card.image_fallback ? `, url(${card.image_fallback})` : ''}`,
            ...(card.image_style || {}),
          }"
          @click.stop
          @click="$emit('click_image')"
        >
          <span class="vb-card__image-corner"></span>
        </div>
      </b-col>
      <b-col
        class="vb-card__element vb-card__element--wrap"
        cols="12"
        :lg="variant === 'horizontal' && card.image && '8'"
      >
        <div class="vb-card__wrap" :style="card.style || {}">
          <div class="vb-card__head" v-if="card.title">
            <h4
              v-if="card.title"
              v-b-popover.hover.top="title_tooltip"
              class="vb-card__title"
              :style="card.title_style || {}"
              v-html="card.title"
              @click.stop
              @click="$emit('click_title')"
            ></h4>
            <p v-if="card.title_badge" v-html="card.title_badge"></p>
            <slot name="title-badge"></slot>
          </div>
          <p class="vb-card__subtitle" v-if="card.subtitle">
            <vb-icon
              v-if="card.subtitle_icon"
              class="vb-card__subtitle-icon"
              :name="card.subtitle_icon.name"
              :color="card.subtitle_icon.color"
              :size="card.subtitle_icon.size"
            />
            <span :class="card.subtitle_class || ''" v-html="card.subtitle" />
          </p>
          <p
            v-if="card.text"
            ref="text_text"
            :id="`text_text-${card.text}-${_uid}`"
            :class="
              'vb-card__text' +
              (card.text_clamped ? ' vb-card__text__clamped' : '')
            "
            v-html="card.text"
          ></p>
          <b-popover
            v-if="text_tooltip"
            :target="`text_text-${card.text}-${_uid}`"
            triggers="hover"
            placement="bottom"
            custom-class="b-popover--dark b-popover--no-arrow"
          >
            <span class="text--base">
              {{ text_tooltip }}
            </span>
          </b-popover>
          <slot name="default"></slot>
          <hr v-if="card.footer" />
          <div
            v-if="card.footer"
            class="w-100 d-flex justify-content-end"
            v-html="card.footer"
          ></div>
          <hr v-if="hasFooterSlot" />
          <div v-if="hasFooterSlot" class="w-100 d-flex justify-content-end">
            <slot name="footer"></slot>
          </div>
        </div>
      </b-col>
    </b-row>
    <!-- if you need it floating on top make it have position absolute -->
    <div v-if="card.badge" v-html="card.badge" />
    <slot name="badge"></slot>
  </div>
</template>

<script>
export default {
  name: "vb-card",

  components: {},

  props: {
    variant: {
      type: String,
      default: "vertical",
    },
    card: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },

  data() {
    return {
      text_tooltip: "",
    };
  },

  computed: {
    hasFooterSlot() {
      return !!this.$slots.footer;
    },

    // il tooltip del titolo è sempre disponibile (anche se il titolo non è
    // troncato) e mostra il nome completo
    title_tooltip() {
      const source = this.card.title_popover || this.card.title || "";
      return source.replace(/<[^>]*>?/gm, "").trim();
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.text_tooltip = this.is_text_truncated(this.$refs.text_text)
        ? this.card.text_popover
        : "";
    });
  },

  methods: {},
};
</script>

<style lang="scss" scoped>
.vb-card {
  $this: &;

  display: flex;
  flex-direction: column;
  min-height: 100%;
  position: relative;

  hr {
    margin: $grid-gutter-width / 6 0px;
  }

  &__wrapper {
    display: flex;
    flex: 1 1 auto;
    overflow: hidden;

    @at-root #{$this}--vertical & {
      flex-direction: column;
    }
  }

  &__element {
    &--wrap {
      display: flex;
      flex: 1 1 auto;
    }
  }

  &__image {
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    border-radius: $border-radius-lg * 2;
    position: relative;
    z-index: 1;

    // standard e type vertical
    min-height: 300px;
    clip-path: polygon(0% 0%, 100% 0%, 100% 102%, 85% 84%, 0 84%);

    @at-root #{$this}--horizontal & {
      @include media-breakpoint-up(lg) {
        min-height: 100%;
        clip-path: polygon(
          0% 0%,
          110% 0%,
          100% 11%,
          92% 31%,
          92% 100%,
          0% 101%
        );
      }
    }

    &-corner {
      width: 30px;
      height: 30px;
      overflow: hidden;
      position: absolute;
      left: 0;
      bottom: 16%;
      z-index: 1;
      transform: scaleX(-1);

      @at-root #{$this}--horizontal & {
        @include media-breakpoint-up(lg) {
          left: auto;
          right: 8%;
          bottom: 0;
          transform: scaleX(1);
        }
      }

      &:before {
        content: "";
        width: 200%;
        height: 200%;
        position: absolute;
        border-radius: $border-radius-lg * 2;
        bottom: 0;
        right: 0;
        box-shadow: 50px 50px 0 0 $vb-body-bg;
      }
    }
  }

  &__wrap {
    background-color: $white;
    padding: $grid-gutter-20;
    border-radius: $border-radius-lg * 2;
    min-height: 100%;
    flex: 1 1 auto;
    // evita che un titolo lungo allarghi la card oltre la colonna
    min-width: 0;

    @at-root #{$this}__element--image + #{$this}__element--wrap & {
      margin-top: -2.375rem;
    }

    @at-root #{$this}--horizontal
        #{$this}__element--image
        + #{$this}__element--wrap
        & {
      @include media-breakpoint-up(lg) {
        margin-top: 0;
        margin-left: -2.5rem;
      }
    }
  }

  &__head {
    & + * {
      margin-top: $grid-gutter-width/6;
      padding-top: $grid-gutter-width/6;
    }

    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    overflow: hidden;
  }

  &__title {
    margin-bottom: 0;
    font-size: $font-size-lg;
    color: $dark;
    font-weight: $vb-font-weight-semibold;

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    // permette al titolo di restringersi (e quindi troncarsi) come flex item
    min-width: 0;
    max-width: 250px;
    position: relative;
    padding-left: $grid-gutter-width / 2;

    // su schermi più grandi il titolo può occupare tutto lo spazio disponibile:
    // tronca solo quando serve davvero
    @include media-breakpoint-up(md) {
      max-width: 100%;
    }

    &:before {
      content: "";
      width: $grid-gutter-width / 5;
      background-color: var(--secondary);
      position: absolute;
      top: 3px;
      bottom: 3px;
      left: 0;
      border-radius: $border-radius;
    }
  }

  &__subtitle {
    margin-bottom: 0;
    color: $gray-500;
    display: flex;
    align-items: center;

    &-icon {
      margin-right: 5px;
    }
  }

  &__text {
    margin-bottom: 0;
    border-top: 1px solid $gray-200;

    &__clamped {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3; /* Limita a 3 righe */

      box-orient: vertical;
      line-clamp: 3;

      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
